import { track } from "./analytics.ts";
import type {
  ApAnalyticsParams,
  ApWarningType,
  InvoiceAnalyticsParams,
  InvoiceExportResult,
} from "./customEvents.types.ts";

/**
 * Custom events.
 *
 * Reference: https://expensa.atlassian.net/wiki/spaces/CSS/pages/1773142027
 *
 */

type WorkspaceCreatedParams = {
  companyId: string;
};
export function trackWorkspaceCreated({
  companyId,
}: WorkspaceCreatedParams): void {
  track("workspace_created", { company_id: companyId });
}

type BankSelectedParams = {
  bankName: string;
};
export function trackBankSelected({ bankName }: BankSelectedParams): void {
  track("bank_selected", { bank_name: bankName });
}

type SkipBankAccountConnectionParams = {
  companyId: string;
};
export function trackSkipBankAccountConnection({
  companyId,
}: SkipBankAccountConnectionParams): void {
  track("skip_bank_account_connection", { company_id: companyId });
}

export function trackBankFlowSuccess(): void {
  track("bank_flow_success");
}

export function trackAccountAssigned(): void {
  track("account_assigned");
}

export type ConsentBannerAction = "accept-all" | "reject-all" | "save";

export type GcmConsent = {
  adPersonalization?: "granted" | "denied";
  adStorage?: "granted" | "denied";
  adUserData?: "granted" | "denied";
  analyticsStorage?: "granted" | "denied";
  adsDataRedaction?: boolean;
};

type ConsentBannerResolvedParams = {
  action: ConsentBannerAction;
  gcm: GcmConsent;
};

export function trackConsentBannerResolved({
  action,
  gcm,
}: ConsentBannerResolvedParams): void {
  track("consent_banner_resolved", { action, gcm });
}

/**
 * Marketplace flow events.
 *
 * Reference: https://expensa.atlassian.net/wiki/spaces/CSS/pages/2107834371
 */

export type ProductAnalyticsParams = {
  productCategory: string;
  productName: string;
  partnerName: string;
};

export type FlowAnalyticsParams = ProductAnalyticsParams & {
  flowId: string;
  flowName: string;
  flowStep: string;
  applicationNumber?: string;
  companyId?: string;
  insuranceType?: string;
  insurancePackage?: string;
};

function toProductWireParams({
  productCategory,
  productName,
  partnerName,
}: ProductAnalyticsParams) {
  return {
    product_category: productCategory,
    product_name: productName,
    partner_name: partnerName,
  };
}

function toFlowWireParams(params: FlowAnalyticsParams) {
  return {
    ...toProductWireParams(params),
    flow_id: params.flowId,
    flow_name: params.flowName,
    flow_step: params.flowStep,
    application_number: params.applicationNumber,
    company_id: params.companyId,
    insurance_type: params.insuranceType,
    insurance_package: params.insurancePackage,
  };
}

export function trackFlowStepView(params: FlowAnalyticsParams): void {
  track("flow_step_view", toFlowWireParams(params));
}

export function trackFlowStepBack(params: FlowAnalyticsParams): void {
  track("flow_step_back", toFlowWireParams(params));
}

export function trackFlowAbandon(params: FlowAnalyticsParams): void {
  track("flow_abandon", toFlowWireParams(params));
}

export function trackFlowComplete(params: FlowAnalyticsParams): void {
  track("flow_complete", toFlowWireParams(params));
}

type FileDownloadParams = FlowAnalyticsParams & {
  fileName: string;
};
export function trackFileDownload({
  fileName,
  ...flowParams
}: FileDownloadParams): void {
  track("file_download", {
    ...toFlowWireParams(flowParams),
    file_name: fileName,
  });
}

type FormParams = FlowAnalyticsParams & {
  formId: string;
};
export function trackFormView({ formId, ...flowParams }: FormParams): void {
  track("form_view", { ...toFlowWireParams(flowParams), form_id: formId });
}

export function trackFormSubmitSuccess({
  formId,
  ...flowParams
}: FormParams): void {
  track("form_submit_success", {
    ...toFlowWireParams(flowParams),
    form_id: formId,
  });
}

type OutboundClickParams = ProductAnalyticsParams & {
  linkUrl: string;
};
export function trackOutboundClick({
  linkUrl,
  ...productParams
}: OutboundClickParams): void {
  track("outbound_click", {
    ...toProductWireParams(productParams),
    link_url: linkUrl,
  });
}

/**
 * Accounts Payable events.
 *
 * Reference: https://expensa.atlassian.net/wiki/spaces/CSS/pages/2225700873
 */

function toApWireParams({ companyId, roleType }: ApAnalyticsParams) {
  return { company_id: companyId, role_type: roleType };
}

function toInvoiceWireParams({
  invoiceId,
  status,
  ...apParams
}: InvoiceAnalyticsParams) {
  return {
    ...toApWireParams(apParams),
    invoice_id: invoiceId,
    status,
  };
}

type ProductActivatedParams = ApAnalyticsParams & {
  productName: string;
};
export function trackProductActivated({
  productName,
  ...apParams
}: ProductActivatedParams): void {
  track("product_activated", {
    ...toApWireParams(apParams),
    product_name: productName,
  });
}

type InvoiceUploadStartParams = ApAnalyticsParams & {
  fileExtension: string;
};
export function trackInvoiceUploadStart({
  fileExtension,
  ...apParams
}: InvoiceUploadStartParams): void {
  track("invoice_upload_start", {
    ...toApWireParams(apParams),
    file_extension: fileExtension,
  });
}

type InvoiceUploadValidationFailParams = InvoiceUploadStartParams & {
  errorCode: string;
};
export function trackInvoiceUploadValidationFail({
  fileExtension,
  errorCode,
  ...apParams
}: InvoiceUploadValidationFailParams): void {
  track("invoice_upload_validation_fail", {
    ...toApWireParams(apParams),
    file_extension: fileExtension,
    error_code: errorCode,
  });
}

export function trackInvoiceDetailView(params: InvoiceAnalyticsParams): void {
  track("invoice_detail_view", toInvoiceWireParams(params));
}

export function trackInvoiceEdited(params: InvoiceAnalyticsParams): void {
  track("invoice_edited", toInvoiceWireParams(params));
}

export function trackInvoiceSentForApproval(
  params: InvoiceAnalyticsParams,
): void {
  track("invoice_sent_for_approval", toInvoiceWireParams(params));
}

export function trackInvoiceApproved(params: InvoiceAnalyticsParams): void {
  track("invoice_approved", toInvoiceWireParams(params));
}

type InvoiceRejectedParams = InvoiceAnalyticsParams & {
  statusReason: string;
};
export function trackInvoiceRejected({
  statusReason,
  ...invoiceParams
}: InvoiceRejectedParams): void {
  track("invoice_rejected", {
    ...toInvoiceWireParams(invoiceParams),
    status_reason: statusReason,
  });
}

export function trackInvoiceSentBackForCorrection(
  params: InvoiceAnalyticsParams,
): void {
  track("invoice_sent_back_for_correction", toInvoiceWireParams(params));
}

export function trackInvoiceApprovalCancelled(
  params: InvoiceAnalyticsParams,
): void {
  track("invoice_approval_cancelled", toInvoiceWireParams(params));
}

type InvoiceExportStartParams = ApAnalyticsParams & {
  invoiceCount: number;
};
export function trackInvoiceExportStart({
  invoiceCount,
  ...apParams
}: InvoiceExportStartParams): void {
  track("invoice_export_start", {
    ...toApWireParams(apParams),
    invoice_count: invoiceCount,
  });
}

type InvoiceExportFinishedParams = ApAnalyticsParams & {
  result: InvoiceExportResult;
};
// The spec names the batch result `status`; it is not an invoice status.
export function trackInvoiceExportFinished({
  result,
  ...apParams
}: InvoiceExportFinishedParams): void {
  track("invoice_export_finished", {
    ...toApWireParams(apParams),
    status: result,
  });
}

type WarningDisplayedParams = InvoiceAnalyticsParams & {
  warningType: ApWarningType;
};
export function trackWarningDisplayed({
  warningType,
  ...invoiceParams
}: WarningDisplayedParams): void {
  track("warning_displayed", {
    ...toInvoiceWireParams(invoiceParams),
    warning_type: warningType,
  });
}
